import {course_getCourseClasses_Api, course_getCourses_Api,} from 'apis'
import {getId} from 'apis/model/base'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {branch, compose, renderNothing} from 'recompose'
import AntdConfigWrapper, {RenderEmptyMeta,} from 'views/AppLocals/custom/AntdConfigWrapper'
import {reactSelectComponents, SelectWidget,} from 'views/AppLocals/custom/ReactSelectComponents'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault} from 'views/Shared'
import {renderIf} from "../../../../Shared";

const FilterSelectField = ({
                             name,
                             value,
                             onChange,
                             options,
                             isLoading,
                             className,
                             ...props
                           }) => (
  <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
    <SelectWidget
      value={_.find(options, {value}) ? value : undefined}
      showSearch={true}
      allowClear={true}
      onChange={(value) => {
        onChange(name, value)
      }}
      loading={!!isLoading}
      optionFilterProp="label"
      className={classNames('CenterSelector w-full', className)}
      options={!!isLoading ? [] : options}
      suffixIcon={
        !!isLoading ? undefined : <reactSelectComponents.DropdownIndicator/>
      }
      {...props}
    />
  </AntdConfigWrapper>
)

const CourseAsyncWrapper = compose(
  branch(
    ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
    renderNothing
  )
)(({owner_id, owner_type, children}) => {
  const [values, setValues] = useState()

  const apiInfo = course_getCourses_Api

  const cacheId = useMemo(
    () =>
      [
        apiInfo.path.replace(':id', owner_id).replace(':type', owner_type),
        JSON.stringify(values),
      ].join('/'),
    [values, owner_id, owner_type]
  )

  const {response, isLoading} = useAsyncWithCache({
    values,
    cacheId,
    apiInfo,
    query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
  })

  return _.isFunction(children)
    ? children({
      isLoading,
      items: getResponseItems(response),
      onSearch: (keyword) => {
        setValues({
          keyword,
        })
      },
    })
    : children
})

const CourseClassAsyncWrapper = compose(
  branch(({course_id}) => _.isEmpty(course_id), renderNothing)
)(({course_id, children}) => {
  const [values, setValues] = useState()

  const apiInfo = course_getCourseClasses_Api

  const cacheId = useMemo(
    () =>
      [apiInfo.path.replace(':id', course_id), JSON.stringify(values)].join(
        '/'
      ),
    [values, course_id]
  )

  const {response, isLoading} = useAsyncWithCache({
    values,
    cacheId,
    apiInfo,
    query: bindQueryParam({id: course_id}),
  })

  return _.isFunction(children)
    ? children({
      isLoading,
      items: getResponseItems(response),
      onSearch: (keyword) => {
        setValues({
          keyword,
        })
      },
    })
    : children
})

export const StudentFilter = ({
                                owner_id,
                                owner_type,
                                defaultValue,
                                value: filter = {
                                  course_id: undefined,
                                  course_class_id: undefined,
                                  sub_class_name: undefined,
                                },
                                onChange: setValues = NullAsDefault,
                              }) => {
  const t = useTranslate()

  const handleChange = useCallback(
    (name, value) => {
      const params = {
        ...filter,
        [name]: value,
      }
      setValues(params)
      return params
    },
    [filter]
  )

  return (
      <div className="flex flex-col items-center gap-2 mt-2">
          <CourseAsyncWrapper
              {...{
                  owner_id,
                  owner_type,
              }}>
              {({items, isLoading}) => {
                  const options = Array.from(items).map((option) => ({
                      value: getId(option),
                      label: getTitle(option),
                  }))
                  return (
                      <div className="flex items-center gap-2 w-full">
                          <span className="text-sm text-color-400 italic whitespace-no-wrap">
                              {`${t('course')} : `}
                          </span>
                          <FilterSelectField
                              name="course_id"
                              loading={isLoading}
                              value={filter.course_id}
                              placeholder={t(isLoading ? 'loading...' : 'course')}
                              options={options}
                              onChange={(name, value) => {
                                  const next = handleChange(name, value)
                                  setValues({
                                      ...(next ?? {}),
                                      course_class_id: undefined,
                                  })
                              }}
                          />
                      </div>
                  )
              }}
          </CourseAsyncWrapper>
          <CourseClassAsyncWrapper {...{course_id: filter.course_id}}>
              {({items, isLoading}) => {
                  const options = Array.from(items).map((option) => ({
                      value: getId(option),
                      label: getTitle(option),
                  }))
                  return (
                      <div className="flex flex-col space-y-3 w-full">
                          <div className="flex items-center gap-2 w-full">
                              <span className="text-sm text-color-400 italic whitespace-no-wrap">
                                  {`${t('classes')} : `}
                              </span>
                              <FilterSelectField
                                  loading={isLoading}
                                  name="course_class_id"
                                  value={filter.course_class_id}
                                  placeholder={t(isLoading ? 'loading...' : 'classes')}
                                  options={options}
                                  onChange={(name, value) => {
                                      const next = handleChange(name, value)
                                      setValues({
                                          ...(next ?? {}),
                                      })
                                  }}
                              />
                          </div>
                          <div className="flex items-center gap-2 w-full">
                              <span className="text-sm text-color-400 italic whitespace-no-wrap">
                                  {`${t('subclass title')} : `}
                              </span>
                              {renderIf(
                                  filter.course_class_id,
                                  <div className="flex flex-wrap gap-y-1 gap-x-2 text-xs">
                                      {_.flatMap(items.filter(e => e.id === filter.course_class_id), ee => ee.sub_class_names)
                                          .filter(e => !!e)
                                          .map((e, i) => (
                                              <span key={i}
                                                    className={classNames("px-3 py-1 flex flex-center cursor-pointer rounded-full no-border font-bold",
                                                        filter?.sub_class_name === e ? "bg-primary hover:bg-primary-50 text-white hover:text-primary-600"
                                                            : "background-200 hover:bg-primary-50 text-color-300 hover:text-primary-600")}
                                                    onClick={() => {
                                                        if (filter?.sub_class_name === e) {
                                                            handleChange("sub_class_name", null)
                                                        } else {
                                                            handleChange("sub_class_name", e)
                                                        }
                                                    }}>
                                                  {e}
                                              </span>
                                          )
                                      )}
                                  </div>
                              )}
                          </div>
                      </div>
                  )
              }}
          </CourseClassAsyncWrapper>
      </div>
  )
}

export default StudentFilter
